import React from 'react';
import { Badge } from '../SubscriptionAppBadge';
import { CallFlagBadge } from '../contact-detail/CallFlagBadge';
import { TrendIndicator } from '../TrendIndicator';
import type { ContactInsights } from '../../types/contactInsights';
import { formatDayMonth, formatShortSpan, getCadence, initialsOf } from '../../utils/contactInsights';
export const insightColumnIds = ['callFlag', 'cadence', 'lastContacted', 'trend', 'skipsPauses', 'clubOneTime', 'openTasks', 'topProduct'] as const;
const muted = <span className="text-sm text-[#94A3B8]" data-id="element-1768" data-mp="7hvyizz5tl61">—</span>;
interface ContactInsightCellProps {
  insights: ContactInsights;
  columnId: string;
}

// Cells for the call-prep columns on the Contacts table
export function ContactInsightCell({
  insights,
  columnId
}: ContactInsightCellProps) {
  switch (columnId) {
    case 'callFlag':
      return insights.callFlag === 'none' ? muted : <CallFlagBadge flag={insights.callFlag} data-id="element-1769" data-mp="r5ll8ss8ov8e" />;
    case 'cadence':
      {
        const cadence = getCadence(insights);
        if (!cadence) return muted;
        return <div className="flex flex-col items-start gap-1" data-id="element-1770" data-mp="3thlvesqi1xjc">
            <span className="text-sm text-[#475569] whitespace-nowrap" data-id="element-1771" data-mp="1crriu4xwkd1">
              Every ~{Math.max(1, Math.round(cadence.gapDays / 7))} wks
            </span>
            {cadence.status === 'lapsed' ? <Badge variant="warning" data-id="element-1772" data-mp="ba658lzhav1n">Lapsed · {formatShortSpan(cadence.days)} overdue</Badge> : cadence.status === 'due-soon' ? <Badge variant="info" data-id="element-1773" data-mp="g87ob6wwmud4">Due soon</Badge> : <Badge variant="success" data-id="element-1774" data-mp="g76c2z45k1tt">On track</Badge>}
          </div>;
      }
    case 'lastContacted':
      return insights.lastContacted ? <span className="text-sm text-[#475569] whitespace-nowrap" data-id="element-1775" data-mp="p0swjt2wtinp">
          {formatDayMonth(insights.lastContacted.date)} · {insights.lastContacted.outcome}
        </span> : <span className="text-sm text-[#94A3B8]" data-id="element-1776" data-mp="u28evddpdb2e">Never</span>;
    case 'trend':
      return <span title="Spend vs previous 12 months" data-id="element-1777" data-mp="yxe0eb370npy">
          <TrendIndicator value={Math.abs(insights.spendTrendPct)} direction={insights.spendTrendPct >= 0 ? 'up' : 'down'} data-id="element-1778" data-mp="13xsbk203cds7" />
        </span>;
    case 'skipsPauses':
      {
        const total = insights.skips + insights.pauses;
        return <span className={`text-sm ${total === 0 ? 'text-[#94A3B8]' : 'text-[#475569]'}`} title={`${insights.skips} ${insights.skips === 1 ? 'skip' : 'skips'} · ${insights.pauses} ${insights.pauses === 1 ? 'pause' : 'pauses'}`} data-id="element-1779" data-mp="9d66c5v3uaz8">
            {total}
          </span>;
      }
    case 'clubOneTime':
      return <span className="text-sm text-[#475569] whitespace-nowrap" title={`${insights.clubOrders} club · ${insights.oneTimeOrders} one-time`} data-id="element-1780" data-mp="wz2xpv5divwf">
          {insights.clubOrders} · {insights.oneTimeOrders}
        </span>;
    case 'openTasks':
      return insights.openTaskAssignees.length === 0 ? muted : <span className="text-sm text-[#475569] whitespace-nowrap" title={`Assigned to ${insights.openTaskAssignees.join(', ')}`} data-id="element-1781" data-mp="120s8raslj9ki">
          {insights.openTaskAssignees.length} · {insights.openTaskAssignees.map(initialsOf).join(', ')}
        </span>;
    case 'topProduct':
      return <span className="text-sm text-[#475569] whitespace-nowrap" data-id="element-1782" data-mp="1janrwhm514kc">{insights.topProduct}</span>;
    default:
      return null;
  }
}